/**********************************************************************
 *   Dynamic Client Configuration (Runtime BASE_URL Detection)
 *
 *   NEXT_PUBLIC_BASE_URL is now OPTIONAL and auto-detected from:
 *   1. Browser: window.location (client-side)
 *   2. Vercel: VERCEL_URL (deployment)
 *   3. Fallback: localhost:3000 (development)
 *   4. Override: NEXT_PUBLIC_BASE_URL (if set)
 *
 *********************************************************************/

import { SellerListing } from "@/interfaces/listing/SellerListing";

/**
 * Get the base URL dynamically based on the current environment
 * Works in browser, SSR, and API routes
 */
function getBaseUrl(): string {
  // 1. Browser context - use window.location
  if (typeof window !== "undefined") {
    return `${window.location.protocol}//${window.location.host}`;
  }

  // 2. Build time - use environment variable if set (optional override)
  if (process.env.NEXT_PUBLIC_BASE_URL) {
    return process.env.NEXT_PUBLIC_BASE_URL;
  }

  // 3. Vercel deployment - use VERCEL_URL
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }

  // 4. Fallback for local development
  return "http://localhost:3000";
}

/**
 * Get the current base URL (cached for performance)
 */
let cachedBaseUrl: string | null = null;

export function getCurrentBaseUrl(): string {
  if (cachedBaseUrl) return cachedBaseUrl;

  cachedBaseUrl = getBaseUrl();
  return cachedBaseUrl;
}

/**
 * Reset cached base URL (useful for testing)
 */
export function resetBaseUrlCache() {
  cachedBaseUrl = null;
}

// Parse base URL into components
const getBaseUrlObject = () => new URL(getCurrentBaseUrl());

// `||`, not `??`: the Dockerfile's `ENV NEXT_PUBLIC_API_IMG_URL=$ARG` bakes an
// EMPTY STRING when the build arg is not passed (the CI build passes none),
// `"" ?? x` keeps "", and `new URL("")` throws - which sent every listing
// photo to the placeholder. Empty must fall back to the current origin.
const imageBase = () =>
  new URL(process.env.NEXT_PUBLIC_API_IMG_URL || getCurrentBaseUrl());

export const publicDomain = () => getBaseUrlObject().hostname;

export function isLive() {
  return publicDomain() === "privateauto.com";
}

export const dealNowDomain = isLive() ? "dealnow.com" : "dnwdev.xyz";

const publicBase = () => getBaseUrlObject();

const makeUrl = (base?: string, path = "/"): string => {
  let urlStr = "";
  let baseHost = base;
  try {
    const baseUrl = getBaseUrlObject();
    const protocol = baseUrl.protocol;
    const port = baseUrl.port;

    if (!base && !publicBase()) return path ?? "/";
    baseHost = new URL(base ?? "", publicBase()).host.replace(/prelogin\./, "");
    urlStr = `${protocol}${protocol.endsWith(":") ? "" : ":"}//${baseHost}`;
    if (port && port != "80" && port != "443" && !urlStr.endsWith(`:${port}`)) {
      urlStr += `:${port}`;
    }
    const url = new URL(path ?? "/", urlStr).toString();
    return url;
  } catch (e: any) {
    console.log(
      `makeUrl(): ${
        baseHost ?? base
      } publicDomain: ${publicDomain()} protocol: ${getBaseUrlObject().protocol} port: ${getBaseUrlObject().port} path: ${path}  [${urlStr}] => ${
        e.message
      }`,
      e
    );
    return "/";
  }
};

export interface ClientConfig {
  hostName: string;
  publicDomain: string;
  isLive: boolean;
  apiUrl: string;
}

/**
 * @deprecated Use useRuntimeConfig() hook instead
 * Hash secret is still compile-time for URL obfuscation (not critical to move to runtime)
 */
export const hashSecret =
  process.env.NEXT_PUBLIC_HASH_SECRET_KEY ?? "whQ5V2C2SEyzhtWYM4j6dA";

/**
 * @deprecated Use useRuntimeConfig().config.googleAutocompleteKey instead
 * Keeping for backward compatibility during migration
 */
export const googleAutocompleteApiKey =
  process.env.NEXT_PUBLIC_GOOGLE_AUTOCOMPLETE_KEY ??
  "AIzaSyABX4LTqTLQGg_b3jFOH8Z6_H5CDqn8tbc";

/**
 * @deprecated Use useRuntimeConfig().config.googleMapsKey instead
 * Keeping for backward compatibility during migration
 */
export const googleMapsApiKey =
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY ??
  "AIzaSyDCoYgKMvU9_kpGutWJEcr74vLFUMv5N88";

/**
 * @deprecated Use useRuntimeConfig().config.googleGeocodeKey instead
 * Keeping for backward compatibility during migration
 */
export const googleGeocodeApiKey =
  process.env.NEXT_PUBLIC_GOOGLE_GEOCODE_KEY ??
  "AIzaSyDCoYgKMvU9_kpGutWJEcr74vLFUMv5N88";

/**
 * @deprecated Use useRuntimeConfig().config.googleRecaptchaKey instead
 * Keeping for backward compatibility during migration
 */
export const googleRecaptchaApiKey =
  process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY ??
  "AIzaSyDCoYgKMvU9_kpGutWJEcr74vLFUMv5N88";

/**
 * @deprecated Use useRuntimeConfig().config.algoliaAppId instead
 * Keeping for backward compatibility during migration
 */
export const alogoliaAppId = process.env.NEXT_PUBLIC_ALGOLIA_APPID ?? "";

/**
 * @deprecated Use useRuntimeConfig().config.algoliaApiKey instead
 * Keeping for backward compatibility during migration
 */
export const alogoliaPublicKey = process.env.NEXT_PUBLIC_ALGOLIA_API_KEY ?? "";

/**
 * @deprecated Use useRuntimeConfig().config.runBuggy instead
 * Keeping for backward compatibility during migration
 */
export const runBuggyClientId = () =>
  (isLive()
    ? process.env.NEXT_PUBLIC_PROD_RUNBUGGY_ID
    : process.env.NEXT_PUBLIC_DEV_RUNBUGGY_ID) ?? "";

/**
 * @deprecated Use useRuntimeConfig().config.runBuggy instead
 * Keeping for backward compatibility during migration
 */
export const runBuggyScriptUrl = () =>
  (isLive()
    ? process.env.NEXT_PUBLIC_PROD_RUNBUGGY_URL
    : process.env.NEXT_PUBLIC_DEV_RUNBUGGY_URL) ?? "";

/**
 * @deprecated Use useRuntimeConfig().config.airbrake instead
 * Keeping for backward compatibility during migration
 */
export const airbrakeConfig = () => ({
  env: isLive() ? "live" : "dev",
  projectId: Number(process.env.NEXT_PUBLIC_AIRBRAKE_PROJECT!),
  projectKey: process.env.NEXT_PUBLIC_AIRBRAKE_KEY!,
});

export const buildId = process.env.PA_BUILD_ID ?? "n/a";

function apiUrl() {
  return makeUrl(process.env.PA_LISTING_API);
}

export function loginAppUrl() {
  if (publicDomain() === "") return "/external-auth";
  return makeUrl(`https://auth.${publicDomain()}`, "/external-auth");
}

export function createClientConfig(): ClientConfig {
  const cfg = {
    hostName: getBaseUrlObject().hostname,
    publicDomain: publicDomain(),
    isLive: isLive(),
    apiUrl: apiUrl(),
  };

  return cfg;
}

export function makeAbsoluteUrl(url: string, def?: string) {
  try {
    if (url) return makeUrl(publicDomain(), url);
  } catch (e: any) {
    console.log(`makeAbsoluteUrl(${url})[${publicDomain()}] - ${e.message}`);
  }
  return def;
}

export function makeApiImage(
  url: string,
  def: string = "/assets/imagePlaceholder.svg"
): string {
  try {
    if (url && url.trim().length > 0) {
      const parts = url.split("/").filter((p) => p.trim().length > 0);
      if (parts[0] == "images") parts.shift();
      if (parts[0] != "static") parts.unshift("static");
      let path = parts.join("/");
      return new URL(path, imageBase()).toString();
    }
  } catch (e: any) {
    console.log(`makeApiImage([${url}]) failed - ${e.message}`);
  }
  return def;
}

export function makeListingImage(url: string, vehicleType: string): string {
  try {
    if (url && url.trim().length > 0) {
      const parts = url.split("/").filter((p) => p.trim().length > 0);
      if (parts[0] == "images") parts.shift();
      if (parts[0] != "static") parts.unshift("static");
      let path = parts.join("/");
      return new URL(path, imageBase()).toString();
    }
  } catch (e: any) {
    console.log(`makeApiImage([${url}]) failed - ${e.message}`);
  }

  return `/assets/listings/new-listing-${
    vehicleType === "Boat" ? "boat" : vehicleType === "RV" ? "rv" : "car"
  }.svg`;
}

export const getDistributionId = () =>
  isLive()
    ? process.env.AWS_PROD_CLOUDFRONT_DISTRIBUTION_ID
    : process.env.AWS_DEV_CLOUDFRONT_DISTRIBUTION_ID;

/**
 * @deprecated Amplify configuration is now loaded dynamically via useRuntimeConfig() in _app.tsx
 * This export is kept for backward compatibility during migration but is no longer used
 *
 * Cognito configuration is now:
 * - Loaded from runtime config (/api/runtime-config)
 * - Initialized lazily in _app.tsx using useEffect
 * - No longer requires compile-time NEXT_PUBLIC_* variables
 *
 * Benefits of new approach:
 * - Zero build arguments required
 * - Deploy same image to any environment
 * - Change auth config without rebuild
 *
 * See: pages/_app.tsx for new initialization pattern
 */
const AWS_REGION = process.env.NEXT_PUBLIC_AWS_REGION ?? "us-east-1";
export const amplifyConfig = {
  aws_project_region: AWS_REGION,
  aws_cognito_identity_pool_id: process.env.NEXT_PUBLIC_COGNITO_IDENTITY_POOL,
  aws_cognito_region: AWS_REGION,
  aws_user_pools_id: process.env.NEXT_PUBLIC_COGNITO_USER_POOL,
  aws_user_pools_web_client_id: process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID,
  Auth: {
    identityPoolId: process.env.NEXT_PUBLIC_COGNITO_IDENTITY_POOL,
    region: AWS_REGION,
    identityPoolRegion: AWS_REGION,
    userPoolId: process.env.NEXT_PUBLIC_COGNITO_USER_POOL,
    userPoolWebClientId: process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID,
  },
};
